import { useCallback, useState } from 'preact/hooks';

import { getTracerModules } from '@apptracer/sdk/lite';

import { useWidget } from '../context/WidgetContext';
import { isRateLimitError } from '../types/guards';
import { storageManager, tokenManager } from '../utils';

import { packageName } from '../../config';
import { getHistoryApi } from '../api/getHistoryApi';
import { HAS_WIDGET_HISTORY } from '../constants/localStorage';

const PER_PAGE = 10;

/** Хук для работы с историей сообщений в виджете */
export const useWidgetHistory = () => {
  const tracerError = getTracerModules(packageName).error;

  const { setMessages, messages, setError, localeMessages } = useWidget();

  // Получаем последнее сообщение в пачке
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;

  // Храним id последнего полученного в пачке сообшения
  const [messageId, setMessageId] = useState(lastMessage?.id);

  // Наличие следующей страницы для загрузки
  const [hasNextPage, setHasNextPage] = useState(true);

  // Искуственное состояние загрузки
  const [isLoading, setIsLoading] = useState(false);

  /** Получение истории */
  const handleGetHistory = useCallback(async () => {
    // Получаем токен
    const token = tokenManager.getToken();
    const isTokenExpired = tokenManager.getIsTokenExpired();

    if (isLoading || !hasNextPage || !token || isTokenExpired) {
      return;
    }

    setIsLoading(true);

    try {
      // Запрашиваем историю
      const result = await getHistoryApi({
        token,
        size: PER_PAGE,
        fromId: messageId,
      });

      if (result) {
        setIsLoading(false);

        // Можно ли запросить еще пачку сообщений
        setHasNextPage(result.hasMore);

        if (result.hasMore) {
          const lastMessageId = result.messages[result.messages.length - 1].id;

          setMessageId(lastMessageId);
        }

        // В момент запроса истории проставляем признак наличия истории,
        // чтобы в дальнейшем знать, что у пользователя была история сообщений
        if (result.messages.length > 0) {
          try {
            storageManager?.setItem(HAS_WIDGET_HISTORY, '1');
          } catch (_) {
            tracerError?.error('Не удалось установить флаг в хранилище', {
              keys: {
                flag: HAS_WIDGET_HISTORY,
              },
            });
          }
        }

        setMessages?.((prevMessages) => [...prevMessages, ...result.messages]);
      }
    } catch (error: unknown) {
      if (isRateLimitError(error)) {
        setError?.(localeMessages.errors.rateLimit);
        tracerError?.error(localeMessages.errors.rateLimit);
        // Останавливаем дальнейшие запросы при ошибке rate limit
        setHasNextPage(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasNextPage, messageId, setMessages, tracerError, setError, localeMessages.errors.rateLimit]);

  return {
    /** Признак наличия следующей страницы для загрузки истории обращений */
    hasNextPage,
    /** Флаг состояния загрузки истории */
    isLoading,
    /** Получение истории */
    handleGetHistory,
  };
};
